import type { PerpMarket } from "@/hooks/airdrop-farming/types";
import type { PerpAssetContext, PerpAssetMeta } from "@cede/trading-data";
import { MarketType } from "@cede/trading-data";
import { assignMarketCategories } from "@/utils/trading";

// ── Adapter ───────────────────────────────────────────────────────────────────

/**
 * Normalizes TradeXYZ (HIP-3 DEX) market data to the generic PerpMarket format.
 *
 * Input comes from Hyperliquid's `metaAndAssetCtxs` endpoint with `dex: "xyz"`.
 * `assets` and `contexts` are parallel arrays (same index = same market).
 */
export function adaptTradeXyzMarkets(assets: PerpAssetMeta[], contexts: PerpAssetContext[]): PerpMarket[] {
  return assets
    .map((asset, index): PerpMarket | null => {
      const ctx = contexts[index];
      if (!ctx || asset.isDelisted) return null;

      const currentPrice = parseFloat(ctx.markPx);
      const prevPrice = parseFloat(ctx.prevDayPx);
      const priceChange24h = prevPrice > 0 ? ((currentPrice - prevPrice) / prevPrice) * 100 : 0;
      const openInterestUsd = parseFloat(ctx.openInterest) * currentPrice;

      return {
        type: MarketType.PERP,
        symbol: `${asset.name}-PERP`,
        pair: `${asset.name.toUpperCase()}/USDC:USDC`,
        baseAsset: asset.name.toUpperCase(),
        quoteAsset: "USDC",
        markPrice: ctx.markPx,
        midPrice: ctx.midPx,
        volume24h: ctx.dayNtlVlm,
        priceChange24h,
        prevDayPrice: ctx.prevDayPx,
        funding: ctx.funding,
        openInterest: openInterestUsd.toString(),
        maxLeverage: asset.maxLeverage,
        szDecimals: asset.szDecimals,
        onlyIsolated: asset.onlyIsolated,
        isDelisted: asset.isDelisted,
        isGrowthMode: asset.growthMode === "enabled",
        searchString: `${asset.name.toLowerCase()}perp`,
        categories: assignMarketCategories(asset.name, MarketType.PERP),
      };
    })
    .filter((m): m is PerpMarket => m !== null);
}
